import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { X, FileCheck, CirclePlus, CircleMinus } from '@/components/icons'
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerFooter } from './ui/drawer'
import { SearchInput } from './ui/search-input'
import { Button } from './ui/button'
import { ListSkeleton } from './ui/skeleton-presets'
import { EmptyState } from './EmptyState'
import { useChecklistTemplates } from '@/lib/hooks/checklist'
import { cn } from '@/lib/utils'

interface TaskPickerDrawerProps {
  isOpen: boolean
  onClose: () => void
  selectedTemplateIds: string[]
  onSelect: (newIds: string[]) => void
}

const sameSet = (a: Set<string>, b: string[]) =>
  a.size === b.length && b.every((id) => a.has(id))

export function TaskPickerDrawer({
  isOpen,
  onClose,
  selectedTemplateIds,
  onSelect,
}: TaskPickerDrawerProps) {
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set(selectedTemplateIds))

  const { data: templates = [], isPending } = useChecklistTemplates()

  useEffect(() => {
    if (isOpen) {
      setSearchQuery('')
      setSelectedIds(new Set(selectedTemplateIds))
    }
  }, [isOpen, selectedTemplateIds])

  const filtered = useMemo(() => {
    if (!searchQuery) return templates
    const q = searchQuery.toLowerCase()
    return templates.filter((t) => t.name.toLowerCase().includes(q))
  }, [templates, searchQuery])

  const toggleSelection = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const hasChanges = !sameSet(selectedIds, selectedTemplateIds)

  const handleAdd = () => {
    onSelect(Array.from(selectedIds))
    onClose()
  }

  const showEmpty = !isPending && templates.length === 0

  return (
    <Drawer open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DrawerContent className="bg-surface-L0 flex flex-col max-h-[85dvh]">
        <DrawerHeader className="flex items-center justify-between p-4 shrink-0">
          <DrawerTitle className="text-lg font-bold text-content-primary-bold tracking-[-1px]">
            Add Task
          </DrawerTitle>
          <Button
            variant="secondary"
            size="icon"
            roundness="round"
            className="size-8"
            onClick={onClose}
          >
            <X className="size-4" />
          </Button>
        </DrawerHeader>

        {showEmpty ? (
          <div className="px-4 py-6 flex flex-col flex-1">
            <EmptyState
              icon={FileCheck}
              title="No checklist templates"
              description="Create a checklist template to attach tasks to this flight."
              addButtonText="Go to Templates"
              onAdd={() => {
                onClose()
                navigate({ to: '/operations/checklisttemplates' })
              }}
            />
          </div>
        ) : (
          <div className="min-h-0 flex-1 overflow-hidden px-4 pb-4">
            <div className="flex flex-col gap-2 h-full">
              <SearchInput
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onClear={() => setSearchQuery('')}
                placeholder="Search tasks"
              />

              <div
                className="overflow-y-auto overscroll-contain h-[360px]"
                style={{ WebkitOverflowScrolling: 'touch' }}
              >
                {isPending ? (
                  <ListSkeleton />
                ) : filtered.length === 0 ? (
                  <p className="text-sm text-content-primary-subtle text-center py-12">
                    No templates match your search
                  </p>
                ) : (
                  <div className="flex flex-col gap-1">
                    {filtered.map((template) => {
                      const isSelected = selectedIds.has(template.id)
                      return (
                        <button
                          key={template.id}
                          type="button"
                          onClick={() => toggleSelection(template.id)}
                          className={cn(
                            'flex items-center gap-3 min-h-9 px-3 py-[7.5px] rounded-md text-left transition-colors',
                            isSelected ? 'bg-surface-L2' : 'hover:bg-surface-L2/50'
                          )}
                        >
                          <div className="flex flex-1 flex-col min-w-0">
                            <span className="text-sm font-semibold text-content-primary-bold leading-5 truncate">
                              {template.name}
                            </span>
                          </div>
                          {isSelected ? (
                            <CircleMinus className="size-6 shrink-0 text-content-primary-bold" />
                          ) : (
                            <CirclePlus className="size-6 shrink-0 text-content-primary-bold" />
                          )}
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {!showEmpty && (
          <DrawerFooter>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="secondary"
                size="mini"
                onClick={onClose}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="mini"
                onClick={handleAdd}
                disabled={!hasChanges}
                className="flex-1"
              >
                Select
              </Button>
            </div>
          </DrawerFooter>
        )}
      </DrawerContent>
    </Drawer>
  )
}
